// Mock data for Chem Carriage module — used by both intranet and portal views
// Type 1 vessels: stainless steel (SS316L) tanks
// Type 2 vessels: zinc silicate coated tanks

export type ChemTank = {
  no: string;
  capacity: number;
  coating: string;
  l3c: string[];
  status: string;
  position: { row: number; side: "P" | "S" | "C" };
};

export type ChemVessel = {
  name: string;
  imo: string;
  type: "Type 1" | "Type 2";
  flag: string;
  location: { lat: number; lon: number; port: string };
  status: string;
  nextPort: string;
  eta: string;
  charterer: string;
  cargoSystem: { igs: string; n2: string; heating: string; pumps: string };
  igs: {
    system: string;
    purity: number;
    o2: number;
    pressure: number;
    dewPoint: number;
    status: "Green" | "Amber" | "Red";
    lastTest: string;
  };
  daysSinceCDIM: number;
  daysSinceOVMSA: number;
  coatingType: string;
  tankCount: number;
  capacityM3: number;
  igsType: string;
  annexCats: string[];
  yearBuilt: number;
  loa: number; beam: number; dwt: number;
  tanks: ChemTank[];
};

const t1Coating = "Stainless Steel 316L";
const t2Coating = "Zinc Silicate";

export const CHEM_VESSELS: ChemVessel[] = [
  {
    name: "Velocity Aurora",
    imo: "9876541",
    type: "Type 2",
    flag: "Marshall Islands",
    location: { lat: 6.83, lon: -57.94, port: "Georgetown, Guyana" },
    status: "Laden",
    nextPort: "Stabroek Block",
    eta: "2026-05-08",
    charterer: "ExxonMobil Chemical",
    cargoSystem: { igs: "green", n2: "green", heating: "green", pumps: "green" },
    igs: { system: "N2 PSA", purity: 99.6, o2: 0.4, pressure: 0.18, dewPoint: -55, status: "Green", lastTest: "2026-05-01" },
    daysSinceCDIM: 42,
    daysSinceOVMSA: 18,
    coatingType: t2Coating,
    tankCount: 8,
    capacityM3: 4320,
    igsType: "N2 PSA",
    annexCats: ["X", "Y", "Z"],
    yearBuilt: 2022,
    loa: 92.5, beam: 21.0, dwt: 4850,
    tanks: [
      { no: "1P", capacity: 540, coating: t2Coating, l3c: ["Methanol", "MEG", "Brine"], status: "Last-Cargo", position: { row: 1, side: "P" } },
      { no: "1S", capacity: 540, coating: t2Coating, l3c: ["MEG", "Methanol", "Brine"], status: "Last-Cargo", position: { row: 1, side: "S" } },
      { no: "2P", capacity: 540, coating: t2Coating, l3c: ["Caustic Soda", "MEG", "Methanol"], status: "Clean", position: { row: 2, side: "P" } },
      { no: "2S", capacity: 540, coating: t2Coating, l3c: ["Sulfuric Acid", "Slop", "Caustic"], status: "Slop", position: { row: 2, side: "S" } },
      { no: "3P", capacity: 540, coating: t2Coating, l3c: ["Brine", "MEG", "Methanol"], status: "Clean", position: { row: 3, side: "P" } },
      { no: "3S", capacity: 540, coating: t2Coating, l3c: ["Methanol", "Brine", "MEG"], status: "Clean", position: { row: 3, side: "S" } },
      { no: "4P", capacity: 540, coating: t2Coating, l3c: ["MEG", "Brine", "Methanol"], status: "Clean", position: { row: 4, side: "P" } },
      { no: "4S", capacity: 540, coating: t2Coating, l3c: ["Caustic", "MEG", "Brine"], status: "Last-Cargo", position: { row: 4, side: "S" } },
    ],
  },
  {
    name: "Velocity Triton",
    imo: "9876542",
    type: "Type 1",
    flag: "Marshall Islands",
    location: { lat: 5.20, lon: -52.12, port: "Paramaribo, Suriname" },
    status: "Loading",
    nextPort: "Stabroek Block",
    eta: "2026-05-06",
    charterer: "TotalEnergies",
    cargoSystem: { igs: "green", n2: "amber", heating: "green", pumps: "green" },
    igs: { system: "N2 Membrane", purity: 98.2, o2: 1.8, pressure: 0.14, dewPoint: -48, status: "Amber", lastTest: "2026-04-29" },
    daysSinceCDIM: 95,
    daysSinceOVMSA: 31,
    coatingType: t1Coating,
    tankCount: 6,
    capacityM3: 3600,
    igsType: "N2 Membrane",
    annexCats: ["X", "Y", "Z", "OS"],
    yearBuilt: 2023,
    loa: 88.0, beam: 20.0, dwt: 4200,
    tanks: [
      { no: "1C", capacity: 600, coating: t1Coating, l3c: ["MDI", "Xylene", "Methanol"], status: "Clean", position: { row: 1, side: "C" } },
      { no: "2C", capacity: 600, coating: t1Coating, l3c: ["TEG", "MEG", "Brine"], status: "Last-Cargo", position: { row: 2, side: "C" } },
      { no: "3P", capacity: 600, coating: t1Coating, l3c: ["MDI", "Xylene", "Brine"], status: "Clean", position: { row: 3, side: "P" } },
      { no: "3S", capacity: 600, coating: t1Coating, l3c: ["TEG", "Methanol", "Brine"], status: "Clean", position: { row: 3, side: "S" } },
      { no: "4P", capacity: 600, coating: t1Coating, l3c: ["MDI", "Xylene", "Methanol"], status: "Last-Cargo", position: { row: 4, side: "P" } },
      { no: "4S", capacity: 600, coating: t1Coating, l3c: ["TEG", "MEG", "Brine"], status: "Clean", position: { row: 4, side: "S" } },
    ],
  },
  {
    name: "Velocity Borealis",
    imo: "9876543",
    type: "Type 2",
    flag: "Marshall Islands",
    location: { lat: 8.94, lon: -64.20, port: "Port of Spain, T&T" },
    status: "Tank Cleaning",
    nextPort: "Georgetown",
    eta: "2026-05-12",
    charterer: "Shell Trading",
    cargoSystem: { igs: "green", n2: "green", heating: "amber", pumps: "green" },
    igs: { system: "N2 PSA", purity: 99.4, o2: 0.6, pressure: 0.17, dewPoint: -52, status: "Green", lastTest: "2026-05-02" },
    daysSinceCDIM: 12,
    daysSinceOVMSA: 64,
    coatingType: t2Coating,
    tankCount: 8,
    capacityM3: 4320,
    igsType: "N2 PSA",
    annexCats: ["Y", "Z"],
    yearBuilt: 2021,
    loa: 92.5, beam: 21.0, dwt: 4900,
    tanks: [
      { no: "1P", capacity: 540, coating: t2Coating, l3c: ["Base Oil", "Drilling Fluid", "Brine"], status: "Under Maintenance", position: { row: 1, side: "P" } },
      { no: "1S", capacity: 540, coating: t2Coating, l3c: ["Brine", "Base Oil", "MEG"], status: "Clean", position: { row: 1, side: "S" } },
      { no: "2P", capacity: 540, coating: t2Coating, l3c: ["MEG", "Brine", "Methanol"], status: "Clean", position: { row: 2, side: "P" } },
      { no: "2S", capacity: 540, coating: t2Coating, l3c: ["Brine", "MEG", "Methanol"], status: "Last-Cargo", position: { row: 2, side: "S" } },
      { no: "3P", capacity: 540, coating: t2Coating, l3c: ["Caustic", "MEG", "Brine"], status: "Clean", position: { row: 3, side: "P" } },
      { no: "3S", capacity: 540, coating: t2Coating, l3c: ["Methanol", "Brine", "MEG"], status: "Clean", position: { row: 3, side: "S" } },
      { no: "4P", capacity: 540, coating: t2Coating, l3c: ["MEG", "Brine", "Methanol"], status: "Clean", position: { row: 4, side: "P" } },
      { no: "4S", capacity: 540, coating: t2Coating, l3c: ["Brine", "MEG", "Methanol"], status: "Clean", position: { row: 4, side: "S" } },
    ],
  },
];

// Compatibility matrix: which cargoes are incompatible with prior cargo residue
export const CARGO_COMPATIBILITY: Record<string, { compatible: string[]; incompatible: string[] }> = {
  "Methanol": { compatible: ["MEG", "TEG", "Brine", "Xylene"], incompatible: ["Sulfuric Acid", "Caustic Soda", "MDI"] },
  "MEG": { compatible: ["Methanol", "TEG", "Brine"], incompatible: ["Sulfuric Acid", "MDI"] },
  "Xylene": { compatible: ["Methanol", "MDI"], incompatible: ["Caustic Soda", "Sulfuric Acid"] },
  "MDI": { compatible: ["Xylene"], incompatible: ["Methanol", "MEG", "Brine", "Caustic Soda"] },
  "Caustic Soda": { compatible: ["Brine"], incompatible: ["Sulfuric Acid", "Methanol", "MDI"] },
  "Sulfuric Acid": { compatible: [], incompatible: ["Methanol", "Caustic Soda", "MEG", "MDI"] },
  "Base Oil": { compatible: ["Drilling Fluid"], incompatible: ["Methanol", "MEG"] },
  "Brine": { compatible: ["MEG", "Methanol", "Caustic Soda"], incompatible: ["MDI"] },
};

export const CHEM_SAMPLES = [
  { id: "QR-001847", type: "Manifold", drawnAt: "Georgetown LP — 2026-05-01 14:22Z", witness: "Saybolt — J. Patel", seal: "VOFF-S-44211", status: "Lab Pending", retainedUntil: "2027-05-01" },
  { id: "QR-001848", type: "Ship's Tank Before", drawnAt: "T1P — 2026-05-01 14:35Z", witness: "Saybolt — J. Patel", seal: "VOFF-S-44212", status: "Pass", retainedUntil: "2027-05-01" },
  { id: "QR-001849", type: "First Foot", drawnAt: "T1P — 2026-05-01 16:02Z", witness: "Saybolt — J. Patel", seal: "VOFF-S-44213", status: "Pass", retainedUntil: "2027-05-01" },
  { id: "QR-001850", type: "Composite", drawnAt: "T1P — 2026-05-01 22:18Z", witness: "Saybolt — J. Patel", seal: "VOFF-S-44214", status: "Pass", retainedUntil: "2027-05-01" },
];

export const CHEM_HSE_KPIS = {
  trir: 0.18, ltif: 0.0, spills: 0, nearMissRate: 12.4, contamination: 0, sampleIntegrity: 100,
};

export const CHEM_VETTING = [
  { vessel: "Velocity Aurora", ovmsa: { last: "2026-04-12", next: "2026-10-12", status: "Current" }, cdim: { last: "2026-03-10", next: "2026-09-10", status: "Current" }, psc: 0 },
  { vessel: "Velocity Triton", ovmsa: { last: "2026-03-30", next: "2026-09-30", status: "Current" }, cdim: { last: "2026-01-22", next: "2026-07-22", status: "Due Soon" }, psc: 1 },
  { vessel: "Velocity Borealis", ovmsa: { last: "2026-02-18", next: "2026-08-18", status: "Current" }, cdim: { last: "2026-04-20", next: "2026-10-20", status: "Current" }, psc: 0 },
];
